import $ from 'jquery';
import _ from 'underscore';
import Backbone from 'backbone';

import { SimpleForm } from '@bingads-webui/simple-form';

import template from './keyword-editor-template.pug';

/**
 * Keyword editor view
 * @private
 */
class KeywordEditor extends Backbone.View {
  /**
   * init Keyword Editor, it will generate a view based on
   * options, and show the view based on the target location
   * @private
   * @param {Object} target - the input element that keyword editor should attach to
   * @param {Object} options - editor model and cursor position info
   * @param {I18n} i18n - i18n object that provides getString
   * @returns {undefined} - initialize view
   */
  initialize({
    target,
    options: {
      defaultText = '',
      keywordCase,
      prefix = '',
      suffix = '',
      updateAction,
    },
    i18n,
  }) {
    this.target = target;

    const keywords = {
      TitleCase: 'KeyWord',
      SentenceCase: 'Keyword',
      LowerCase: 'keyword',
    };

    const schema = {
      name: 'Keyword',
      type: 'object',
      properties: {
        defaultText: {
          type: 'string',
        },
        keywordCase: {
          type: 'string',
          enum: [..._.keys(keywords)],
        },
        syntax: {
          type: 'string',
        },
      },
      required: ['defaultText'],
    };

    const fields = [
      {
        property: 'defaultText',
        label: i18n.getString('KeywordEditor_DefaultText_Label'),
        validators: {
          required: {
            message: i18n.getString('KeywordEditor_DefaultText_RequiredValidationMessage'),
          },
        },
      },
      {
        property: 'keywordCase',
        label: i18n.getString('KeywordEditor_Case_Label'),
        control: 'radios',
        valueMessages: {
          TitleCase: i18n.getString('KeywordEditor_Case_TitleCase'),
          SentenceCase: i18n.getString('KeywordEditor_Case_SentenceCase'),
          LowerCase: i18n.getString('KeywordEditor_Case_LowerCase'),
        },
      },
      {
        property: 'syntax',
        label: {
          depends: ['keywordCase'],
          computedBy(kCase) {
            const keywordText = keywords[kCase];
            return `${i18n.getString('AdCustomizers_Editor_Syntax_Prefix_Label')} {${keywordText}:${i18n.getString('KeywordEditor_DefaultText')}}`;
          },
        },
      },
    ];

    const model = {
      defaultText,
      keywordCase,
    };

    this.form = new SimpleForm({
      schema,
      fields,
      model,
      template,
      i18n,
    });

    this.form.on('save', (newModel) => {
      const keywordText = keywords[newModel.keywordCase];
      const newValue = `${prefix}{${keywordText}:${newModel.defaultText}}${suffix}`;
      const cursorPosition = newValue.length - suffix.length;

      updateAction(newValue);
      target.setSelectionRange(cursorPosition, cursorPosition);
      target.focus();

      hideKeywordEditor(); // eslint-disable-line no-use-before-define
    });

    this.form.on('cancel', () => hideKeywordEditor()); // eslint-disable-line no-use-before-define

    return this;
  }

  render() {
    const $target = $(this.target);
    const position = $target.offset();

    position.top += $target.outerHeight();

    this.$el.css({
      position: 'absolute',
      visibility: 'visible',
      left: position.left,
      top: position.top,
    });

    this.$el.append(this.form.render().el);

    // Clicking on elements having 'ac-helper-area' class won't hide currently visible helper
    this.$el.addClass('ac-helper-area adCustomizersEditor');

    return this;
  }
}

/**
 * TODO: Bug 957242, refactor out this singleton
 * Editor instance (used by hideKeywordEditor() and show() to update,
 * register/deregister events and change visibility)
 * @private
 */
let editor = null;

/**
 * Hides keyword editor when user clicks outside of ad customizer helper areas and date picker areas
 * @param {object} event - click event
 * @returns {undefined}
 * @private
 */
function clickedAway(event) {
  const target = $(event.target);

  if (target.closest('.ac-helper-area').length === 0) {
    hideKeywordEditor(); // eslint-disable-line no-use-before-define
  }
}

/**
 * Handles key down events.
 * Hides editor when user hits escape.
 * @param {object} event - click event
 * @returns {undefined}
 * @private
 */
function keydown(event) {
  const keyCode = event.key || event.code;
  switch (keyCode) {
    case 'Escape':
      hideKeywordEditor(); // eslint-disable-line no-use-before-define
      break;
    default:
      break;
  }
}

/**
 * Hides keyword editor and deregisters clickaway event.
 * @returns {undefined}
 */
export function hideKeywordEditor() {
  if (editor) {
    $(editor.target).off('keydown', keydown);
    $(window).off('click', clickedAway);
    editor.remove();
    editor = null;
  }
}

/**
 * Shows keyword editor and registers to global click events.
 * @param {object} target - element the editor will be attached to.
 * @param {object} options - editor model and cursor position info.
 * @param {string} options.defaultText - text show in the editor input box
 * @param {string} options.keywordCase - keyword case enum. TitleCase/SentenceCase/LowerCase
 * @param {string} options.prefix - string before keyword customizer
 * @param {string} options.suffix - string after keyword customizer
 * @param {function} options.updateAction - function to update input box text value
 * @param {I18n} i18n - i18n object that provides getString
 * @returns {undefined}
 */
export function showKeywordEditor(target, options, i18n) {
  hideKeywordEditor();

  editor = new KeywordEditor({ target, options, i18n });
  $('body').append(editor.render().el);
  $(window).on('click', clickedAway);
  $(target).on('keydown', keydown);
}

/**
 * Checks whether keyword editor is visible
 * @returns {bool} true if visible
 */
export function isKeywordEditorVisible() {
  return editor !== null;
}
